
import React from 'react'
import {Shield, Database, Eye, UserCheck, Lock, RefreshCw, Mail} from 'lucide-react'

const Privacy = () => {
  return (
    <div className="min-h-screen py-20 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <Shield className="h-16 w-16 text-primary-teal mx-auto mb-6" />
          <h1 className="text-4xl md:text-5xl font-bold text-primary-navy mb-6">
            Privacy Policy
          </h1>
          <p className="text-xl text-primary-slate">
            Your privacy matters to us. Here's how we protect and use your information.
          </p>
        </div>

        <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-200">
          <div className="space-y-12">
            
            {/* What we collect */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Database className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">What We Collect</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <p>When you register with Nursing Hive, we collect:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Basic profile information: name, email, country, education level, and experience</li>
                  <li>Professional details: nursing specialties, goals, and preferred destination countries</li>
                  <li>Certification status: NCLEX, CGFNS, Dataflow, IELTS/OET progress</li>
                  <li>Credentials (optional): documents you upload for verification</li>
                  <li>Communication data: messages you send through our contact forms</li>
                </ul>
              </div>
            </section>

            {/* Why we collect it */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Eye className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">Why We Collect This Information</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <p>We use your data to:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Provide personalized guidance for your nursing career journey</li>
                  <li>Match you with ethical recruiters after you opt in</li>
                  <li>Send relevant updates and resources</li>
                  <li>Respond to your queries and improve our services</li>
                </ul>
              </div>
            </section>

            {/* Who sees it */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <UserCheck className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">Who Can See Your Information</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <div className="bg-primary-light p-6 rounded-lg">
                  <p className="font-semibold text-primary-navy mb-2">Your profile remains private until you choose to share it.</p>
                  <p>Only Nursing Hive administrators can view your full data; recruiters see only anonymized details until you opt in.</p>
                </div>
                <p>We never sell your information and only share with:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Verified recruiters you approve</li>
                  <li>Secure service providers (under confidentiality agreements)</li>
                  <li>Legal authorities if required by law</li>
                </ul>
              </div>
            </section>

            {/* Data rights */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <UserCheck className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">Your Data Rights</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <p>You can:</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li><strong>Access:</strong> Request a copy of your stored data</li>
                  <li><strong>Update:</strong> Modify your details anytime</li>
                  <li><strong>Delete:</strong> Permanently remove your account</li>
                  <li><strong>Opt-out:</strong> Withdraw consent for recruiter matching</li>
                </ul>
                <p className="mt-4">Email: <a href="mailto:hello@nursinghive.com" className="text-primary-teal hover:underline font-semibold">hello@nursinghive.com</a></p>
              </div>
            </section>

            {/* Security */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <Lock className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">How We Protect Your Data</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Encryption in transit and at rest</li>
                  <li>Limited access for authorized personnel</li>
                  <li>Regular security reviews</li>
                  <li>No payment data stored on our servers</li>
                </ul>
              </div>
            </section>

            {/* Updates */}
            <section>
              <div className="flex items-center space-x-3 mb-6">
                <RefreshCw className="h-6 w-6 text-primary-teal" />
                <h2 className="text-2xl font-bold text-primary-navy">Policy Updates</h2>
              </div>
              <div className="space-y-4 text-primary-slate">
                <p>Nursing Hive may update this Privacy Policy.</p>
                <p>Significant changes will be announced by email and on this page.</p>
              </div>
            </section>

          </div>

          <div className="mt-12 pt-8 border-t border-gray-200 text-center text-sm text-primary-slate">
            <p>Last updated: November 2025</p>
            <p className="mt-2">© 2025 Nursing Hive — Ethical international nursing mobility.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Privacy
